import { useCallback, useEffect, useRef } from 'react';
import type { JSONContent } from '@tiptap/react';
import { useAuthStore } from '../stores/useAuthStore';

// A draft is stored under `borgee:composer:draft:<accountId>:<draftKey>`, where
// the draft key is `channel:<channelId>` for a channel or DM composer,
// `thread:<threadId>` for a reply composer, and
// `pending-thread:<originMessageId>` for the composer whose first send creates
// the thread.
const DRAFT_STORAGE_PREFIX = 'borgee:composer:draft:';

const WRITE_DEBOUNCE_MS = 500;

// A draft untouched for 30 days is abandoned, and abandonment is the only thing
// that ever removes one that was neither sent nor signed out from — a surface
// can stop existing without its composer mounting again (a pending thread a
// colleague answered first, a channel that was left), so without an age limit
// storage only grows.
const DRAFT_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

export const channelDraftKey = (channelId: string): string => `channel:${channelId}`;

export const threadDraftKey = (threadId: string): string => `thread:${threadId}`;

/**
 * A thread that does not exist yet has no id to key its composer by, and the
 * parent channel's id is already taken by that channel's own composer, so the
 * message the thread will hang off names the draft instead.
 */
export const pendingThreadDraftKey = (originMessageId: string): string => `pending-thread:${originMessageId}`;

/**
 * A draft holds the editor's ProseMirror document, not its markdown. A mention
 * serializes to the `<@id>` wire form the server resolves, which markdown
 * parsing reads back as escaped literal text — a restored draft would then send
 * a mention of nobody. The document round-trips every node exactly.
 *
 * `savedAt` is what makes a draft expirable; see `DRAFT_RETENTION_MS`.
 */
interface StoredDraft {
  doc: JSONContent;
  savedAt: number;
}

/**
 * A draft belongs to the member who typed it, and the account id is part of the
 * storage key rather than a field inside the record: a key another account
 * cannot name is a draft it cannot read, hydrate, or send under its own name,
 * without that protection depending on any cleanup code having run. It has to
 * hold on its own, because a session can end without running the sign-out path
 * at all — an expired token, or a closed tab followed by someone else signing
 * in on the same browser profile.
 */
const signedInAccountId = (): string | null => useAuthStore.getState().currentUser?.id ?? null;

const storageKey = (accountId: string, draftKey: string): string =>
  `${DRAFT_STORAGE_PREFIX}${accountId}:${draftKey}`;

// localStorage throws instead of answering under private browsing and when the
// origin is out of quota, and a value that is not a draft record is not ours to
// interpret. A draft is a convenience layered on the composer, so every
// accessor degrades to "nothing persisted" rather than propagating out of a
// keystroke handler and breaking typing.
const readRecord = (key: string): StoredDraft | null => {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as StoredDraft;
    if (typeof parsed?.savedAt !== 'number') return null;
    if (typeof parsed?.doc !== 'object' || parsed.doc === null) return null;
    return parsed;
  } catch {
    return null;
  }
};

const readStoredDraft = (accountId: string, draftKey: string): StoredDraft | null =>
  readRecord(storageKey(accountId, draftKey));

/** Answers whether the write landed, which is what lets a hand-over between two keys refuse to drop its source. */
const writeStoredDraft = (accountId: string, draftKey: string, doc: JSONContent | null): boolean => {
  try {
    if (doc === null) {
      window.localStorage.removeItem(storageKey(accountId, draftKey));
    } else {
      const record: StoredDraft = { doc, savedAt: Date.now() };
      window.localStorage.setItem(storageKey(accountId, draftKey), JSON.stringify(record));
    }
    return true;
  } catch {
    // A composer that cannot persist must still accept typing.
    return false;
  }
};

const draftStorageKeys = (): string[] => {
  const keys: string[] = [];
  try {
    // The whole index is collected before anything is removed, since removing
    // an entry renumbers the ones after it.
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key !== null && key.startsWith(DRAFT_STORAGE_PREFIX)) keys.push(key);
    }
  } catch {
    return [];
  }
  return keys;
};

const removeStorageKey = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing to do about storage that refuses to forget.
  }
};

// The newest keystrokes of a mounted composer live in its debounce window
// rather than in storage, so anything that reads or moves a stored draft from
// outside a composer has to flush every mounted one first or it works from text
// the member has already replaced.
const mountedComposerFlushes = new Set<() => void>();

export const flushComposerDrafts = (): void => {
  for (const flush of [...mountedComposerFlushes]) flush();
};

/**
 * Hands the draft of a superseded surface to the key that surface acquired — a
 * pending thread composer whose thread now exists. The composer being left
 * behind is normally still mounted and still holding its last keystrokes, so
 * every live composer is flushed before the source is read. Text already
 * waiting under the new key wins, and the old key only goes away once the text
 * is safe: it is dropped when the destination holds it, and kept when storage
 * refused to take it.
 */
export const migrateDraft = (fromKey: string, toKey: string): void => {
  const accountId = signedInAccountId();
  if (accountId === null) return;
  flushComposerDrafts();
  const carried = readStoredDraft(accountId, fromKey);
  if (carried === null) return;
  const destinationOccupied = readStoredDraft(accountId, toKey) !== null;
  if (!destinationOccupied && !writeStoredDraft(accountId, toKey, carried.doc)) return;
  writeStoredDraft(accountId, fromKey, null);
};

/**
 * Ends an account's drafts along with its session. Account-scoped keys already
 * keep the next member who signs in from reading them; this is what keeps a
 * member's unsent text from outliving the sitting they typed it in on a shared
 * browser profile. The account is passed in rather than read back out of the
 * auth store, because sign-out is the moment that store stops naming them.
 */
export const discardAccountDrafts = (accountId: string): void => {
  const prefix = `${DRAFT_STORAGE_PREFIX}${accountId}:`;
  for (const key of draftStorageKeys()) {
    if (key.startsWith(prefix)) removeStorageKey(key);
  }
};

/** Drops every draft past the retention window, plus anything stored under the prefix that is not a draft record. */
export const evictStaleDrafts = (): void => {
  const cutoff = Date.now() - DRAFT_RETENTION_MS;
  for (const key of draftStorageKeys()) {
    const draft = readRecord(key);
    if (draft !== null && draft.savedAt > cutoff) continue;
    removeStorageKey(key);
  }
};

export interface ComposerDraft {
  /**
   * Reads the document stored for the current key. Its identity changes only
   * with the key and the account that owns it, so a hydration effect depending
   * on this callback fires exactly once per surface and never while the member
   * is typing.
   */
  restore: () => JSONContent | null;
  /**
   * Records the composer's document under the current key; `null` means the
   * composer is empty and the entry is removed. The write is debounced, and
   * flushed on a key change, on unmount, and when the page goes away.
   */
  record: (doc: JSONContent | null) => void;
  /**
   * Drops the stored draft named by `draftKey`, and cancels a pending write
   * only when that write was recorded under the same key. A send that awaits a
   * round trip can land after the composer has been swapped onto another
   * surface, and the draft it is entitled to delete is the one it was issued
   * under — never whichever one is on screen when it returns.
   */
  clear: (draftKey: string) => void;
}

/**
 * Keeps a composer's unsent document in localStorage under `draftKey`, owned by
 * the account that is signed in.
 *
 * The debounce exists to keep a keystroke off the storage path, never to defer
 * a loss: every way the composer can go away flushes the pending write first —
 * a key change, unmount, `pagehide`, a hidden tab — and so does every read of
 * stored drafts from outside a composer, via `flushComposerDrafts`.
 */
export const useComposerDraft = (draftKey: string): ComposerDraft => {
  const accountId = useAuthStore(s => s.currentUser?.id ?? null);
  const keyRef = useRef(draftKey);
  const accountRef = useRef(accountId);
  // The pending write carries the account and key it was recorded under, so a
  // flush caused by a key change still writes the outgoing composer's text
  // under the outgoing key rather than stamping it onto the incoming one.
  const pendingRef = useRef<{ accountId: string; key: string; doc: JSONContent | null } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelScheduledWrite = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const flush = useCallback(() => {
    cancelScheduledWrite();
    const pending = pendingRef.current;
    if (pending === null) return;
    pendingRef.current = null;
    // Signing out discards the account's drafts while its composer is still
    // mounted; the flush that unmount causes must not write one back.
    if (pending.accountId !== signedInAccountId()) return;
    writeStoredDraft(pending.accountId, pending.key, pending.doc);
  }, [cancelScheduledWrite]);

  // The key is adopted in an effect rather than during render because the
  // composer still shows the outgoing key's text until the hydration effect
  // that runs right after this one replaces it.
  useEffect(() => {
    keyRef.current = draftKey;
    accountRef.current = accountId;
    return flush;
  }, [draftKey, accountId, flush]);

  useEffect(() => {
    mountedComposerFlushes.add(flush);
    return () => { mountedComposerFlushes.delete(flush); };
  }, [flush]);

  // A reload or a closed tab never unmounts the composer, so without this the
  // debounce window would swallow the last keystrokes — the exact loss this
  // hook exists to prevent. `pagehide` covers a navigation or a close the tab
  // is present for; a backgrounded mobile tab can instead be discarded outright
  // with `visibilitychange` as its last event, so both are flush points.
  useEffect(() => {
    const flushWhenHidden = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', flushWhenHidden);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', flushWhenHidden);
    };
  }, [flush]);

  useEffect(evictStaleDrafts, []);

  const restore = useCallback(
    () => (accountId === null ? null : readStoredDraft(accountId, draftKey)?.doc ?? null),
    [accountId, draftKey],
  );

  // Nobody owns a draft typed while no account is signed in, so there is
  // nowhere to file it: the composer keeps that text in memory only.
  const record = useCallback((doc: JSONContent | null) => {
    const owner = accountRef.current;
    if (owner === null) return;
    pendingRef.current = { accountId: owner, key: keyRef.current, doc };
    cancelScheduledWrite();
    timerRef.current = setTimeout(flush, WRITE_DEBOUNCE_MS);
  }, [flush, cancelScheduledWrite]);

  const clear = useCallback((key: string) => {
    if (pendingRef.current?.key === key) {
      cancelScheduledWrite();
      pendingRef.current = null;
    }
    const owner = accountRef.current;
    if (owner === null) return;
    writeStoredDraft(owner, key, null);
  }, [cancelScheduledWrite]);

  return { restore, record, clear };
};
